import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Users, User, X } from 'lucide-react'
import { RecipientPicker } from './RecipientPicker'
import { GroupRosterPanel } from './GroupRosterPanel'
import type { RecipientChips } from './useRecipientChips'
import { cn } from '@/lib/utils'

interface RecipientFieldProps {
  field: RecipientChips
  /** Field caption — "To" on a broadcast, "Members" on a group chat. */
  label?: string
  placeholder?: string
}

/**
 * The chip + search "To" field: type a name, pick whole groups or individuals
 * out of the dropdown, remove with the ×. Used by both the broadcast composer
 * and the new-group-chat dialog so the two behave identically.
 *
 * A group chip opens: "SMS.ED-Clerk-GMCH" tells you nothing about who is behind
 * it, least of all that two of them have no mobile number on file. Clicking the
 * chip shows the roster without leaving the message you're writing.
 */
export function RecipientField({
  field,
  label = 'To',
  placeholder = 'Add groups or people…',
}: RecipientFieldProps) {
  const { chips, query, setQuery, pickerOpen, setPickerOpen, hasChips, removeChip } = field
  const containerRef = useRef<HTMLDivElement>(null)
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)

  // Close both dropdowns on outside click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPickerOpen(false)
        setOpenGroupId(null)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [setPickerOpen])

  // A chip removed while its roster is open would leave the panel showing a
  // group that is no longer a recipient.
  useEffect(() => {
    if (openGroupId && !chips.some((c) => c.kind === 'group' && c.id === openGroupId)) {
      setOpenGroupId(null)
    }
  }, [chips, openGroupId])

  const openPicker = () => {
    setPickerOpen(true)
    setOpenGroupId(null)
  }

  return (
    <div ref={containerRef}>
      <div className="mb-[7px] text-[12px] font-bold uppercase tracking-[0.03em] text-muted-foreground">
        {label}
      </div>
      <div
        className="flex flex-wrap items-center gap-[7px] rounded-xl border-[1.5px] border-border bg-surface p-[9px]"
        onClick={openPicker}
      >
        {chips.map((chip) => {
          const isGroup = chip.kind === 'group'
          const expanded = isGroup && openGroupId === chip.id
          return (
            <span
              key={`${chip.kind}-${chip.id}`}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border-[1.5px] py-[5px] pl-[11px] pr-[9px] text-[13px] font-bold',
                isGroup
                  ? 'border-primary bg-primary-tint text-primary'
                  : 'border-teal bg-teal-tint text-teal',
              )}
            >
              {isGroup ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setPickerOpen(false)
                    setOpenGroupId(expanded ? null : chip.id)
                  }}
                  className="inline-flex items-center gap-1.5"
                  aria-expanded={expanded}
                  aria-label={`Show who is in ${chip.name}`}
                >
                  <Users className="h-[13px] w-[13px]" />
                  {chip.name}
                  <ChevronDown
                    className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')}
                  />
                </button>
              ) : (
                <>
                  <User className="h-[13px] w-[13px]" />
                  {chip.name}
                </>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  removeChip(chip)
                }}
                className="opacity-70 hover:opacity-100"
                aria-label={`Remove ${chip.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )
        })}
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            openPicker()
          }}
          onFocus={openPicker}
          placeholder={hasChips ? 'Add more…' : placeholder}
          className="min-w-[120px] flex-1 border-none bg-transparent p-[5px] text-[13.5px] text-foreground outline-none"
        />
      </div>

      {openGroupId && (
        <GroupRosterPanel groupId={openGroupId} onClose={() => setOpenGroupId(null)} />
      )}

      {pickerOpen && (
        <RecipientPicker
          query={query}
          segment={field.segment}
          setSegment={field.setSegment}
          chosenGroupIds={field.chosenGroupIds}
          chosenPersonIds={field.chosenPersonIds}
          onAddGroup={field.addGroup}
          onAddPerson={field.addPerson}
        />
      )}
    </div>
  )
}
